import { useEffect, useState } from "react";
import { useServiceWorkerUpdate } from "@/hooks/useServiceWorkerUpdate";
import { Button } from "@/components/ui/button";
import { RefreshCw, Sparkles } from "lucide-react";

/**
 * Shows a sticky banner when a new service worker is ready.
 * Auto-applies after a brief grace period so most users never see it,
 * but provides a clear manual "Update now" path as fallback.
 */
const AUTO_APPLY_DELAY_MS = 1500;
const BANNER_FALLBACK_MS = 2500;

const AppUpdatePrompt = () => {
  const { updateAvailable, applyUpdate } = useServiceWorkerUpdate();
  const [visible, setVisible] = useState(false);
  const [applying, setApplying] = useState(false);

  useEffect(() => {
    if (!updateAvailable) return;

    // Show banner immediately so user sees something is happening
    setVisible(true);

    // Try silent auto-apply shortly after — most users get a seamless reload
    const autoTimer = window.setTimeout(() => {
      setApplying(true);
      applyUpdate();
    }, AUTO_APPLY_DELAY_MS);

    // Fallback: if reload hasn't happened, banner is already visible for manual click
    const bannerTimer = window.setTimeout(() => {
      setVisible(true);
    }, BANNER_FALLBACK_MS);

    return () => {
      window.clearTimeout(autoTimer);
      window.clearTimeout(bannerTimer);
    };
  }, [updateAvailable, applyUpdate]);

  if (!updateAvailable || !visible) return null;

  return (
    <div
      role="status"
      aria-live="polite"
      className="fixed top-3 left-1/2 -translate-x-1/2 z-floating-ui w-[calc(100%-1.5rem)] max-w-md animate-in fade-in slide-in-from-top-2 duration-300"
    >
      <div className="rounded-2xl border border-primary/20 bg-card/95 backdrop-blur-md shadow-elegant px-4 py-3 flex items-center gap-3">
        <div className="flex-shrink-0 w-9 h-9 rounded-full bg-primary/10 flex items-center justify-center">
          <Sparkles className="w-4 h-4 text-primary" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="font-semibold text-sm text-foreground">Ny version tillgänglig</div>
          <div className="text-xs text-muted-foreground">Uppdatera för senaste funktionerna</div>
        </div>
        <Button
          size="sm"
          onClick={() => {
            setApplying(true);
            applyUpdate();
          }}
          disabled={applying}
          className="flex-shrink-0"
        >
          {applying ? (
            <RefreshCw className="w-3.5 h-3.5 animate-spin" />
          ) : (
            <>
              <RefreshCw className="w-3.5 h-3.5 mr-1.5" />
              Uppdatera
            </>
          )}
        </Button>
      </div>
    </div>
  );
};

export default AppUpdatePrompt;
